import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { LedgerService, LedgerBalance } from '@/services';

interface LedgerState {
  balances: LedgerBalance[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  // Customer ledger entries pagination
  customerLedgerEntries: any[];
  customerLedgerLoading: boolean;
  customerLedgerError: string | null;
  customerLedgerPagination: {
    page: number;
    hasMore: boolean;
    total: number;
    isLoadingMore: boolean;
  };
}

const initialState: LedgerState = {
  balances: [],
  loading: false,
  error: null,
  searchQuery: '',
  // Customer ledger entries pagination
  customerLedgerEntries: [],
  customerLedgerLoading: false,
  customerLedgerError: null,
  customerLedgerPagination: {
    page: 1,
    hasMore: true,
    total: 0,
    isLoadingMore: false,
  },
};

export const fetchLedgerBalances = createAsyncThunk(
  'ledger/fetchBalances',
  async (storeId: string, { rejectWithValue }) => {
    try {
      return await LedgerService.getBalances(storeId);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch balances');
    }
  }
);

export const fetchCustomerLedgerEntriesPaginated = createAsyncThunk(
  'ledger/fetchCustomerLedgerEntriesPaginated',
  async ({ storeId, customerPhone, page }: {
    storeId: string;
    customerPhone: string;
    page: number;
  }, { rejectWithValue }) => {
    try {
      return await LedgerService.getCustomerLedgerWithAllocationsPaginated(storeId, customerPhone, page);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch customer ledger entries');
    }
  }
);

const ledgerSlice = createSlice({
  name: 'ledger',
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    resetCustomerLedgerPagination: (state) => {
      state.customerLedgerPagination = {
        page: 1,
        hasMore: true,
        total: 0,
        isLoadingMore: false,
      };
    },
    clearCustomerLedgerEntries: (state) => {
      state.customerLedgerEntries = [];
      state.customerLedgerError = null;
      state.customerLedgerPagination = {
        page: 1,
        hasMore: true,
        total: 0,
        isLoadingMore: false,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchLedgerBalances.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLedgerBalances.fulfilled, (state, action) => {
        state.loading = false;
        state.balances = action.payload;
      })
      .addCase(fetchLedgerBalances.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Customer ledger entries pagination
      .addCase(fetchCustomerLedgerEntriesPaginated.pending, (state, action) => {
        const { page } = action.meta.arg;
        if (page === 1) {
          state.customerLedgerLoading = true;
        } else {
          state.customerLedgerPagination.isLoadingMore = true;
        }
        state.customerLedgerError = null;
      })
      .addCase(fetchCustomerLedgerEntriesPaginated.fulfilled, (state, action) => {
        const { page } = action.meta.arg;
        const { data, hasMore, total } = action.payload;
        
        if (page === 1) {
          state.customerLedgerEntries = data;
          state.customerLedgerLoading = false;
        } else {
          state.customerLedgerEntries = [...state.customerLedgerEntries, ...data];
          state.customerLedgerPagination.isLoadingMore = false;
        }
        
        state.customerLedgerPagination = {
          ...state.customerLedgerPagination,
          page,
          hasMore,
          total,
          isLoadingMore: false,
        };
      })
      .addCase(fetchCustomerLedgerEntriesPaginated.rejected, (state, action) => {
        const { page } = action.meta.arg;
        if (page === 1) {
          state.customerLedgerLoading = false;
        } else {
          state.customerLedgerPagination.isLoadingMore = false;
        }
        state.customerLedgerError = action.payload as string;
      });
  },
});

export const { setSearchQuery, resetCustomerLedgerPagination, clearCustomerLedgerEntries } = ledgerSlice.actions;
export default ledgerSlice.reducer; 